import { Subject } from "rxjs";
export interface location_service {
  longitude: number;
  latitude: number;
  bearing: number;
  speed: number;
  altitude: number;
  /** Vertical accuracy (m), when the source reports one. */
  altitudeAccuracy?: number | null;
  isInterpolatedVal: boolean;
  accuracy: number;
}

export interface signalStatusIf {
  signalOld: boolean;
  signalReliable: boolean;
  signalAccuracyMeters: number;
}

export class gpsLocationSmoother {
  public outLocation = new Subject<location_service>(); // this is the published "new" gps location for the app or implementation

  public signalStatus = new Subject<signalStatusIf>(); // this is the published gps status indicating whether old or not accura

  // timing
  private lastPush: number = 0;
  private deltaT: number = 0; // number of miliseconds between the most recent pair of locations

  // cache
  private point0: location_service;
  private point1: location_service;

  // output paras
  private outputFrequency: number = 100; // these are miliseconds per value

  private blockInput: boolean = false;

  // degraded operation / signal old
  private signalAccuracyTooLow: boolean = false;
  private signalTooOld: boolean = false;
  private signalAccuracy: number = 0;
  private signalTooOld_latch: boolean = false;
  private signalAccuracyTooLow_latch: boolean = false;
  private signalAccuracy_latch: number = 0;

  private lastSignalTimestamp: number = 0;

  private signalWarnThresold: number = 80;
  private signalMaxAge: number = 15;

  constructor() {
    // here is the signal watcher
    setInterval(() => {
      // detect signal too old here
      this.signalTooOld = false;

      const now: number = new Date().getTime();
      const age: number = (now - this.lastSignalTimestamp) / 1000;

      if (age > this.signalMaxAge) {
        this.signalTooOld = true;
      }

      // the other values are being detected in "pushGeoLocation"
      // ...

      const signalState: signalStatusIf = {
        signalReliable: !this.signalAccuracyTooLow && !this.signalTooOld,
        signalOld: this.signalTooOld,
        signalAccuracyMeters: Math.round(this.signalAccuracy),
      };

      this.signalStatus.next(signalState);

      // Icon hiding on an unreliable signal is driven by signalStatus
      // subscribers (the map toggles the aircraft layer visibility).
      // outLocation only ever carries real positions.

      this.signalAccuracy_latch = this.signalAccuracy;
      this.signalAccuracyTooLow_latch = this.signalAccuracyTooLow;
      this.signalTooOld_latch = this.signalTooOld;
    }, 5000);
  }

  public async pushGeoLocation(lsi: location_service, bypass: boolean = false) {
    this.lastSignalTimestamp = new Date().getTime();

    // check signal
    this.signalAccuracy = lsi.accuracy;
    this.signalAccuracyTooLow = this.signalAccuracy > this.signalWarnThresold;

    if (this.blockInput) {
      return;
    }

    if (lsi.latitude == undefined) {
      return;
    }

    this.deltaT = new Date().getTime() - this.lastPush;
    this.lastPush = new Date().getTime();
    this.point1 = lsi;

    this.blockInput = true;

    // do calc (create valueLists for each value)

    if (this.point0 !== undefined) {
      // bearing modulo 360
      this.point0.bearing = this.point0.bearing % 360;
      this.point1.bearing = this.point1.bearing % 360;

      let latList = this.createValueList(this.point0.latitude, this.point1.latitude, this.deltaT);
      let lonList = this.createValueList(this.point0.longitude, this.point1.longitude, this.deltaT);
      let altList = this.createValueList(this.point0.altitude, this.point1.altitude, this.deltaT);

      let hdgList = this.createValueList(lsi.bearing, lsi.bearing, this.deltaT);
      let speedList = this.createValueList(this.point0.speed, this.point1.speed, this.deltaT);

      const maxTime = new Date().getTime() + latList.length * this.outputFrequency * 0.9;

      // if less 20 m/s, do not interpolate
      if (lsi.speed < 3000 || bypass) {
        let rawLocation: location_service = {
          latitude: lsi.latitude,
          longitude: lsi.longitude,
          altitude: lsi.altitude,
          altitudeAccuracy: lsi.altitudeAccuracy,
          bearing: lsi.bearing,
          speed: lsi.speed,
          isInterpolatedVal: false,
          accuracy: lsi.accuracy,
        };
        this.point0 = lsi;
        if (!this.signalAccuracyTooLow) this.outLocation.next(rawLocation);
        this.blockInput = false;
        return;
      } else {
        for (let i = 0; i < latList.length; i++) {
          let newLocation: location_service = {
            latitude: latList[i],
            longitude: lonList[i],
            altitude: altList[i],
            altitudeAccuracy: lsi.altitudeAccuracy,
            bearing: hdgList[i],
            speed: speedList[i],
            isInterpolatedVal: i > 0,
            accuracy: -1,
          };

          if (new Date().getTime() > maxTime) {
            console.log("exit interpolation loop!");
            break;
          } else {
            if (!this.signalAccuracyTooLow) this.outLocation.next(newLocation);
            console.log(" INT GPS POINT > " + JSON.stringify(newLocation));
            await this.delay(this.outputFrequency);
          }
        }
      }
    } else {
      // add init point, if not yet a pair avail
      let newLocation: location_service = {
        latitude: this.point1.latitude,
        longitude: this.point1.longitude,
        altitude: this.point1.altitude,
        altitudeAccuracy: this.point1.altitudeAccuracy,
        bearing: this.point1.bearing,
        speed: this.point1.speed,
        isInterpolatedVal: false,
        accuracy: -1,
      };
      if (!this.signalAccuracyTooLow) this.outLocation.next(newLocation);
    }

    this.point0 = lsi;

    this.blockInput = false;
  }

  private createValueList(startVal: number, endVal: number, dt: number): number[] {
    // number of values
    const nbrOfValues: number = dt / this.outputFrequency;

    // the return array
    let retValues: number[] = [];

    // differentiator
    const diff = (endVal - startVal) / nbrOfValues;

    for (let i = 0; i < nbrOfValues; i++) {
      retValues.push(startVal + i * diff);
    }

    return retValues;
  }

  private delay(ms: number) {
    // eslint-disable-next-line promise/avoid-new
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
